import React, { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import BrandLogo from "./BrandLogo";

/* ------------------------------------------------------------------ */
/* Mobile list (unchanged behavior — flat vertical list)               */
/* ------------------------------------------------------------------ */
const mobileNav = [
  { label: "About", to: "/about" },
  { label: "School Format", to: "/school-format" },
  { label: "Academics", to: "/academics" },
  { label: "Electives & Faith", to: "/electives" },
  { label: "School Life", to: "/school-life" },
  { label: "Learning & Placement", to: "/learning-placement" },
  { label: "Tuition & Admissions", to: "/admissions" },
  { label: "Inquire", to: "/inquire" },
];

/* ------------------------------------------------------------------ */
/* Desktop groups                                                      */
/* ------------------------------------------------------------------ */
const desktopGroups = [
  {
    label: "About",
    items: [
      { label: "About", to: "/about" },
      { label: "School Format", to: "/school-format" },
    ],
  },
  {
    label: "Our Program",
    items: [
      { label: "Academics", to: "/academics" },
      { label: "Electives & Faith", to: "/electives" },
      { label: "School Life", to: "/school-life" },
    ],
  },
  {
    label: "Admissions",
    items: [
      { label: "Tuition & Admissions", to: "/admissions" },
      { label: "Learning & Placement", to: "/learning-placement" },
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Single desktop dropdown — opens on hover, keyboard focus, or click  */
/* ------------------------------------------------------------------ */
const DesktopDropdown = ({ label, items, pathname }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);
  const activeChild = items.some((i) => i.to === pathname);

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const handleBlur = (e) => {
    if (wrapRef.current && !wrapRef.current.contains(e.relatedTarget)) {
      setOpen(false);
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={closeSoon}
      onFocus={openMenu}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`nav-group-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`nav-link font-sans-nav text-[12px] 2xl:text-[12.5px] tracking-[0.2em] 2xl:tracking-[0.22em] uppercase whitespace-nowrap inline-flex items-center gap-1 hover:text-[#c9a961] focus:outline-none focus-visible:text-[#c9a961] ${
          activeChild ? "text-[#c9a961]" : "text-[#1e2952]"
        }`}
      >
        {label}
        <ChevronDown
          size={14}
          strokeWidth={2}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {/* Panel — invisible when closed, but a small hover-bridge pt-3 keeps mouse in group */}
      <div
        role="menu"
        className={`absolute left-0 top-full pt-3 min-w-[220px] transition-opacity duration-150 ${
          open ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
      >
        <div className="bg-[#fbf7ee] border border-[#e6dfc8] shadow-[0_10px_30px_-12px_rgba(30,41,82,0.25)] py-2">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              data-testid={`nav-item-${item.to.replace("/", "")}`}
              className={`block px-5 py-2.5 font-sans-nav text-[11.5px] tracking-[0.22em] uppercase whitespace-nowrap hover:bg-[#f2ead4] hover:text-[#c9a961] ${
                pathname === item.to ? "text-[#c9a961]" : "text-[#1e2952]"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Navbar                                                              */
/* ------------------------------------------------------------------ */
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 bg-[#fbf7ee] transition-shadow ${
        scrolled ? "shadow-[0_1px_0_rgba(30,41,82,0.08)]" : ""
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex items-center justify-between py-5 md:py-6">
          <BrandLogo
            testId="header-logo-link"
            className="w-[232px] sm:w-[256px] md:w-[288px] lg:w-[280px] xl:w-[320px] shrink-0"
          />

          {/* Desktop — grouped */}
          <nav className="hidden xl:flex items-center gap-8 2xl:gap-10" aria-label="Primary">
            {desktopGroups.map((g) => (
              <DesktopDropdown key={g.label} label={g.label} items={g.items} pathname={pathname} />
            ))}
            <Link
              to="/inquire"
              data-testid="nav-inquire-link"
              className={`nav-link font-sans-nav text-[12px] 2xl:text-[12.5px] tracking-[0.2em] 2xl:tracking-[0.22em] uppercase whitespace-nowrap hover:text-[#c9a961] ${
                pathname === "/inquire" ? "text-[#c9a961]" : "text-[#1e2952]"
              }`}
            >
              Inquire
            </Link>
          </nav>

          {/* Mobile toggle */}
          <button
            aria-label="Menu"
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
            className="xl:hidden p-2 text-[#1e2952]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile menu — unchanged flat list */}
        {open && (
          <div className="xl:hidden pb-6 border-t border-[#e6dfc8] pt-4">
            <nav className="flex flex-col gap-4" aria-label="Mobile primary">
              {mobileNav.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={`font-sans-nav text-[13px] tracking-[0.22em] uppercase py-1 ${
                    pathname === item.to ? "text-[#c9a961]" : "text-[#1e2952]"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
